import React, { useContext, useEffect, useState } from "react";
import { EditableCheckbox } from "../../Atoms/inputs/EditableCheckbox";
import { EditableFormAttributeValueProperty } from "../../Atoms/EditableProperties/EditableFormAttributeValueProperty";
import { WindowContext } from "../../Service/Context/WindowContext";
import { DocumentService } from "../../Service/DocumentService";
import { FormatS } from "../../Service/FormatS";
import { Text, Tooltip, useMantineTheme } from "@mantine/core";
import { isDefined, printify } from "../../Utils/utils";
import { patchDocumentPosition } from "../../Service/restapi/sharedContractService";
import { useToasts } from "react-toast-notifications";
import { getDocumentTranslator } from "../Document/documentLanguage";
import { useLingui } from "@lingui/react";
import { Icon } from "../../Atoms/Icons";
import {
  clampPositionAmount,
  getMaxPositionAmount,
  getMinPositionAmount,
  isNegotiableAmountEditable,
  parsePositionAmount,
  shouldAutoSelectOptionalPosition,
  shouldHidePositionAmountAndPriceUnit,
  shouldHidePositionPrices,
} from "./positionDisplay";

// eslint-disable-next-line import/no-anonymous-default-export
export default ({
  _document,
  formSubmission,
  position,
  taxType,
  values,
  handleChange,
  patchSubmissionValue,
  columnStyles,
  onPositionChange,
  hideGroupPrices = false,
}) => {
  const { windowWidth } = useContext(WindowContext);
  const { addToast } = useToasts();
  const theme = useMantineTheme();
  const { currency } = _document;
  const { i18n } = useLingui();
  const t = getDocumentTranslator(i18n, _document.language);
  const { discount = {}, price, netPrice, tax } = position;
  const { amount: discountAmount = 0 } = discount;
  const activePriceTier = DocumentService.resolvePriceTier(position);
  const priceUnit = DocumentService.resolvePriceUnit(position);
  const priceTiers = getDisplayPriceTiers(position.priceTiers);
  const isFreeText = position.type === "FREE_TEXT";
  const minAmount = getMinPositionAmount(position.min);
  const maxAmount = getMaxPositionAmount(position.max);
  const [amount, setAmount] = useState(getInitialAmount(position.amount, minAmount, maxAmount));
  const [amountLoading, setAmountLoading] = useState(false);
  const [amountError, setAmountError] = useState(false);
  const negotiableAmountEditable = isNegotiableAmountEditable(
    position,
    _document.documentType
  );
  const hideAmountAndPriceUnit = shouldHidePositionAmountAndPriceUnit(
    position,
    _document.documentType
  );
  const hideDocumentPrices = shouldHidePositionPrices(_document.documentType);
  const hidePrices = shouldHidePositionPrices(
    _document.documentType,
    hideGroupPrices
  );
  const firstColumnWidth = columnStyles.firstColumn.width;
  const priceColumnWidth = columnStyles.priceColumn.width;
  const showOptionalPositionColumn = firstColumnWidth > 0;
  const optionalPositionColumn = showOptionalPositionColumn
    ? `${firstColumnWidth}px `
    : "";
  const textColumnIndex = showOptionalPositionColumn ? 2 : 1;
  const numericColumnCount = windowWidth > 800 && !hideDocumentPrices ? 3 : 1;
  const totalColumnIndex = textColumnIndex + numericColumnCount;
  const textColumnEndIndex = hidePrices
    ? totalColumnIndex + 1
    : totalColumnIndex;
  const desktopGridColumns = hideDocumentPrices
    ? `${optionalPositionColumn}minmax(0, 1fr) ${priceColumnWidth}px`
    : `${optionalPositionColumn}minmax(0, 1fr) repeat(3, ${priceColumnWidth}px)`;
  const compactGridColumns = `${optionalPositionColumn}minmax(0, 1fr) ${priceColumnWidth}px`;

  useEffect(() => {
    setAmount(getInitialAmount(position.amount, minAmount, maxAmount));
  }, [position.amount, minAmount, maxAmount]);

  let styles = {
    container: {
      display: "grid",
      gridTemplateColumns: isFreeText
        ? `${optionalPositionColumn}minmax(0, 1fr)`
        : windowWidth > 800
          ? desktopGridColumns
          : compactGridColumns,
      padding: isFreeText ? "14px 0" : "12px 0",
    },
    label: {
      fontWeight: isFreeText ? 600 : 400,
      color: "#30343a",
      lineHeight: 1.4,
      overflowWrap: "break-word",
      wordWrap: "break-word",
    },
    subtext: {
      marginTop: 4,
      color: "#6d6d6d",
      fontSize: 13,
      lineHeight: 1.45,
    },
    firstColumn: {
      ...columnStyles.firstColumn,
    },
    textColumn: {
      ...columnStyles.textColumn,
      gridColumn:
        !isFreeText &&
        hideAmountAndPriceUnit &&
        (windowWidth > 800 || hidePrices)
          ? `${textColumnIndex} / ${textColumnEndIndex}`
          : undefined,
    },
    priceColumn: {
      ...columnStyles.priceColumn,
    },
    totalColumn: {
      ...columnStyles.priceColumn,
      gridColumn: totalColumnIndex,
    },
    amountInput: {
      width: 74,
      maxWidth: 74,
      marginLeft: "auto",
    },
    amountInputWrapper: {
      display: "flex",
      alignItems: "center",
      justifyContent: "flex-end",
      gap: 4,
    },
  };

  let patchField = position.customAttributeId
    ? position.customAttributeId
    : position.id;
  let keys = [];
  let obj;

  for (obj in values) {
    if (values[obj]) {
      keys.push(obj);
    }
  }

  if (isFreeText) {
    return (
      <div style={styles.container}>
        {showOptionalPositionColumn && <div style={styles.firstColumn} />}
        <div style={styles.textColumn}>
          <div style={styles.label}>
            <div dangerouslySetInnerHTML={{ __html: position.label }} />
          </div>
          <div
            className={"description"}
            style={styles.subtext}
            dangerouslySetInnerHTML={{ __html: position.subtext }}
          />
        </div>
      </div>
    );
  }

  const commitAmount = (newAmount) => {
    const requestedAmount = parsePositionAmount(newAmount);
    const nextAmount = clampPositionAmount(newAmount, minAmount, maxAmount);
    const shouldSelectPosition = shouldAutoSelectOptionalPosition(
      position,
      nextAmount,
      values[patchField]
    );

    const selectPosition = () => {
      if (
        !shouldSelectPosition ||
        typeof patchSubmissionValue !== "function"
      ) {
        return Promise.resolve();
      }

      return patchSubmissionValue(patchField, true);
    };

    setAmount(nextAmount);

    if (isDefined(maxAmount) && requestedAmount > maxAmount) {
      addToast(`Die angefragte Menge übersteigt die verfügbare Kapazität. Für diese Position können maximal ${maxAmount} bereitgestellt werden.`, {
        appearance: "info",
        autoDismiss: true,
      });
    }

    if (`${nextAmount}` === `${position.amount}` || !_document || !_document.id) {
      selectPosition().catch((error) => {
        setAmountError(true);
        console.error("could not select optional document position", error);
      });
      return;
    }

    setAmountLoading(true);
    setAmountError(false);

    patchDocumentPosition(_document.id, position.id, {
      amount: `${nextAmount}`,
    })
      .then((resp) => {
        if (resp.ok) {
          return resp.json();
        }
        throw resp;
      })
      .then((updatedPosition) => {
        if (typeof onPositionChange === "function") {
          onPositionChange(updatedPosition);
        }

        return selectPosition();
      })
      .catch((error) => {
        setAmountError(true);
        console.error("could not patch document position amount", error);
      })
      .finally(() => setAmountLoading(false));
  };

  const renderAmount = () => {
    if (!negotiableAmountEditable) {
      return (
        <div style={styles.amountInputWrapper}>
          {renderPriceTierTooltip()}
          <span>
            {position.amount} {position.unit}
          </span>
        </div>
      );
    }

    return (
      <div style={styles.amountInputWrapper}>
        {renderPriceTierTooltip()}
        <input
          type="text"
          inputMode="decimal"
          pattern="[0-9]+([.,][0-9]+)?"
          value={amount}
          disabled={formSubmission.lastSubmitted}
          onChange={(event) => setAmount(event.currentTarget.value)}
          onBlur={(event) => commitAmount(event.currentTarget.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.currentTarget.blur();
            }
          }}
          style={{
            ...styles.amountInput,
            textAlign: "right",
            height: 28,
            minHeight: 28,
            paddingLeft: 8,
            paddingRight: 4,
            borderColor: amountError ? "red" : undefined,
          }}
          className={`form-control${amountLoading ? " loading" : ""}`}
        />
        {isDefined(position.unit) && (
          <Text size="sm">{printify(position.unit)}</Text>
        )}
      </div>
    );
  };

  const renderPriceTierTooltip = () => {
    if (hidePrices || !position.negotiable || priceTiers.length === 0) {
      return null;
    }

    return (
      <Tooltip
        withArrow
        withinPortal
        multiline
        width={280}
        position="bottom-start"
        openDelay={150}
        events={{ hover: true, focus: true, touch: true }}
        label={
          <div>
            <Text size="xs" weight={700} style={priceTierTooltipHeaderStyle}>
              {t("priceTiersTitle")}
            </Text>
            {priceTiers.map((priceTier, index) => {
              const isActive = priceTier === activePriceTier;
              const tierPrice = DocumentService.calculateNetGrossPrice(
                taxType,
                DocumentService.getPriceTierPrice(priceTier),
                tax
              );

              return (
                <div
                  key={`${priceTier.from}-${priceTier.to}-${index}`}
                  style={getPriceTierTooltipRowStyle(isActive)}
                >
                  <Text size="xs" weight={isActive ? 700 : 400} inherit>
                    {formatPriceTierRange(priceTier, t)}
                  </Text>
                  <div style={priceTierTooltipPriceStyle}>
                    <Text size="xs" weight={isActive ? 700 : 400} inherit>
                      {FormatS.formatPriceWithCurrency(currency, tierPrice)} /{" "}
                      {printify(position.unit) || t("unit")}
                    </Text>
                    {isActive && (
                      <span style={activePriceTierLabelStyle}>
                        ✓ {t("priceTierCurrent")}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        }
      >
        <button
          type="button"
          aria-label={t("priceTiersTitle")}
          style={{
            ...priceTierTooltipButtonStyle,
            color: theme.colors.blue[6],
          }}
        >
          <Icon value="TAGS" />
        </button>
      </Tooltip>
    );
  };

  return (
    <>
      <div style={styles.container}>
        {showOptionalPositionColumn && (
          <div style={styles.firstColumn}>
            {position.optional && (
              <EditableFormAttributeValueProperty
                formSubmissionId={formSubmission.id}
                patchField={patchField}
                changeHandler={(p) => {
                  handleChange(patchField, p[patchField]);
                }}
                entity={values}
                values={keys}
                labelValue={position.id + ""}
                editFormatter={EditableCheckbox}
                multiple={false}
                editOnly={true}
                disabled={formSubmission.lastSubmitted}
              />
            )}
          </div>
        )}
        <div style={styles.textColumn}>
          <div style={styles.label}>
            <div dangerouslySetInnerHTML={{ __html: position.label }} />
          </div>
          <div
            className={"description"}
            style={styles.subtext}
            dangerouslySetInnerHTML={{ __html: position.subtext }}
          />
        </div>
        {windowWidth > 800 && !hideAmountAndPriceUnit && (
          <div style={styles.priceColumn}>
            {renderAmount()}
          </div>
        )}
        {windowWidth > 800 && !hidePrices && !hideAmountAndPriceUnit && (
          <div style={styles.priceColumn}>
            {isDefined(priceUnit) && (
              <div>
                {FormatS.formatPriceWithCurrency(
                  currency,
                  DocumentService.calculateNetGrossPrice(
                    taxType,
                    priceUnit,
                    tax
                  )
                )}
              </div>
            )}
          </div>
        )}
        {!hidePrices && (
          <div style={styles.totalColumn}>
            <div>
              {windowWidth < 800 &&
                !hideAmountAndPriceUnit &&
                (negotiableAmountEditable ? (
                  renderAmount()
                ) : (
                  <div style={styles.amountInputWrapper}>
                  {renderPriceTierTooltip()}
                  {!(isAmountOne(position.amount) && !isDefined(position.unit)) && (
                    <Text size="xs" color="dimmed">{`${position.amount} ${printify(
                      position.unit
                    )}`}</Text>
                  )}
                </div>
                ))}
              <b>
                {FormatS.formatPriceWithCurrency(
                  currency,
                  taxType === "net" ? netPrice : price
                )}
              </b>
            </div>
            {discountAmount > 0 && (
              <Text size="xs">{`${t("discount")}: ${FormatS.formatPriceWithCurrency(
                currency,
                discountAmount
              )}`}</Text>
            )}
          </div>
        )}
        {hidePrices && windowWidth < 800 && !hideAmountAndPriceUnit && (
          <div style={styles.totalColumn}>{renderAmount()}</div>
        )}
      </div>
    </>
  );
};

const getInitialAmount = (amount, minAmount, maxAmount) => {
  return clampPositionAmount(amount, minAmount, maxAmount);
};

const isAmountOne = (amount) => {
  return Number(amount) === 1;
};

const getDisplayPriceTiers = (priceTiers) => {
  if (!Array.isArray(priceTiers)) {
    return [];
  }

  return priceTiers.filter(
    (priceTier) =>
      priceTier &&
      Object.prototype.hasOwnProperty.call(priceTier, "from") &&
      Object.prototype.hasOwnProperty.call(priceTier, "to") &&
      Object.prototype.hasOwnProperty.call(priceTier, "price") &&
      isDefined(DocumentService.getPriceTierPrice(priceTier))
  );
};

const formatPriceTierRange = ({ from, to }, t) => {
  if (!isDefined(to)) {
    return `${t("priceTierFrom")} ${from}`;
  }

  return `${from}\u2013${to}`;
};

const getPriceTierTooltipRowStyle = (isActive) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 12,
  padding: "6px 7px",
  borderRadius: 4,
  backgroundColor: isActive ? "#e6fcf5" : "transparent",
  color: isActive ? "#087f5b" : "#ffffff",
});

const activePriceTierLabelStyle = {
  color: "#087f5b",
  fontSize: 9,
  fontWeight: 700,
  whiteSpace: "nowrap",
};

const priceTierTooltipHeaderStyle = {
  color: "#ffffff",
  marginBottom: 4,
};

const priceTierTooltipPriceStyle = {
  display: "flex",
  alignItems: "flex-end",
  flexDirection: "column",
  textAlign: "right",
};

const priceTierTooltipButtonStyle = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
  padding: 2,
  border: 0,
  background: "transparent",
  cursor: "help",
  fontSize: 14,
  lineHeight: 1,
};
